import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight, NotebookPen, Pencil } from "lucide-react";
import { useState } from "react";
import {
  currentDecisions,
  hubNextDocuments,
  hubNextStep,
  type HubRecord,
  type ToolHub,
} from "../../shared/tool-hubs";
import type { Artifact } from "../../shared/customer-context-schemas";
import { Button } from "../components/ui/button";
import { ContentLoading } from "../components/ContentLoading";
import { FieldError } from "../components/ui/field";
import { Sheet } from "../components/ui/sheet";
import { listArtifacts } from "./api";
import { CustomerArtifacts } from "./CustomerArtifacts";
import { CustomerSearch } from "./CustomerKnowledge";
import { HubComposer } from "./HubComposer";
import { HubEditForm } from "./HubEditForm";
import { HubTimeline } from "./HubTimeline";
import { buildTimeline, type TimelineGroup } from "./hub-timeline";
import { OperationsLink } from "./OperationsLink";
import type { OperationsNavigate } from "./navigation";
import { ToolHubContract } from "./ToolHubContract";
import { ToolDecisions, ToolFacts, ToolKnowledge } from "./ToolHubSidebar";
import {
  customerPageKeys,
  getHubAssignments,
  getToolHub,
  getToolHubDocuments,
  listToolHubRecords,
} from "./tool-hubs-api";
import { usePrimaryContact } from "./use-primary-contact";

export type HubPermissions = {
  canRead: boolean;
  canWrite: boolean;
  canUpload: boolean;
  canSearch: boolean;
  canReadFiles: boolean;
  canReadBilling: boolean;
  canManageBilling: boolean;
  canUsePages: boolean;
};

type Customer = { id: string; slug: string; name: string; ready: boolean };

function useHubData(organizationId: string, hubId: string, permissions: HubPermissions) {
  const key = ["ops", "tool-hub", organizationId, hubId] as const;
  const hub = useQuery({
    queryKey: [...key, "hub"],
    queryFn: () => getToolHub(organizationId, hubId),
    enabled: permissions.canRead,
  });
  const records = useQuery({
    queryKey: [...key, "records"],
    queryFn: () => listToolHubRecords(organizationId, hubId),
    enabled: permissions.canRead,
  });
  const files = useQuery({
    queryKey: [...key, "files"],
    queryFn: () => listArtifacts(organizationId, { kind: "tool", toolId: hubId }),
    enabled: permissions.canReadFiles,
  });
  const documents = useQuery({
    queryKey: [...key, "documents"],
    queryFn: () => getToolHubDocuments(organizationId, hubId),
    enabled: permissions.canReadBilling,
  });
  const assignments = useQuery({
    queryKey: customerPageKeys.assignments(organizationId),
    queryFn: () => getHubAssignments(organizationId),
    enabled: permissions.canRead,
  });
  return { key, hub, records, files, documents, assignments };
}

export function ToolHubPage({
  customer,
  hubId,
  navigate,
  permissions,
}: {
  customer: Customer;
  hubId: string;
  navigate: OperationsNavigate;
  permissions: HubPermissions;
}) {
  const data = useHubData(customer.id, hubId, permissions);
  if (!permissions.canRead)
    return <FieldError>Your employee permissions do not include customer context.</FieldError>;
  if (data.hub.isPending || data.records.isPending)
    return <ContentLoading label="Loading tool…" variant="cards" />;
  if (!data.hub.data || !data.records.data)
    return <FieldError>{unavailable(data.hub.error ?? data.records.error)}</FieldError>;
  const library = data.assignments.data ? data.assignments.data.library : [];
  return (
    <HubBody
      hub={data.hub.data.hub}
      records={data.records.data.records}
      files={data.files.data ? data.files.data.artifacts : []}
      docs={data.documents.data ?? null}
      assigned={library.filter((row) => row.hubId === hubId)}
      queryKey={data.key}
      customer={customer}
      navigate={navigate}
      permissions={permissions}
    />
  );
}

const unavailable = (error: Error | null) => error?.message ?? "This tool is not available.";

type Sheets = "log" | "edit" | null;

function useHubRefresh(queryKey: readonly unknown[], organizationId: string) {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey });
    void client.invalidateQueries({ queryKey: ["ops", "tool-hubs", organizationId] });
    void client.invalidateQueries({ queryKey: ["ops", "customers", organizationId, "artifacts"] });
  };
}

function HubBody({
  hub,
  records,
  files,
  docs,
  assigned,
  queryKey,
  customer,
  navigate,
  permissions,
}: {
  hub: ToolHub;
  records: HubRecord[];
  files: Artifact[];
  docs: Awaited<ReturnType<typeof getToolHubDocuments>> | null;
  assigned: Parameters<typeof ToolKnowledge>[0]["assigned"];
  queryKey: readonly unknown[];
  customer: Customer;
  navigate: OperationsNavigate;
  permissions: HubPermissions;
}) {
  const [now] = useState(() => Math.floor(Date.now() / 1000));
  const [sheet, setSheet] = useState<Sheets>(null);
  const refresh = useHubRefresh(queryKey, customer.id);
  const next = hubNextStep({
    hub,
    records,
    documents: docs ? hubNextDocuments(docs.documents.linked) : [],
    billing: docs?.billing ?? null,
    now,
  });
  const close = () => {
    setSheet(null);
  };
  return (
    <section className="space-y-6" aria-labelledby="tool-hub-title">
      <BackToTools customerSlug={customer.slug} navigate={navigate} />
      <HubHeader hub={hub} canWrite={permissions.canWrite} onOpen={setSheet} />
      <NextStep text={next.text} due={next.rule !== "nothing_due"} />
      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
        <HubMain
          hub={hub}
          records={records}
          files={files}
          docs={docs}
          customer={customer}
          navigate={navigate}
          permissions={permissions}
          onLinked={refresh}
        />
        <HubAside
          hub={hub}
          records={records}
          assigned={assigned}
          customer={customer}
          navigate={navigate}
          permissions={permissions}
          onEdit={() => {
            setSheet("edit");
          }}
        />
      </div>
      <HubSheets
        sheet={sheet}
        hub={hub}
        records={records}
        customer={customer}
        canUpload={permissions.canUpload}
        onClose={close}
        onSaved={() => {
          close();
          refresh();
        }}
      />
    </section>
  );
}

function HubMain({
  hub,
  records,
  files,
  docs,
  customer,
  navigate,
  permissions,
  onLinked,
}: {
  hub: ToolHub;
  records: HubRecord[];
  files: Artifact[];
  docs: Awaited<ReturnType<typeof getToolHubDocuments>> | null;
  customer: Customer;
  navigate: OperationsNavigate;
  permissions: HubPermissions;
  onLinked: () => void;
}) {
  return (
    <div className="min-w-0 space-y-8">
      <ToolHubContract
        organizationId={customer.id}
        customerSlug={customer.slug}
        hubId={hub.id}
        documents={docs?.documents ?? null}
        canManage={permissions.canManageBilling}
        navigate={navigate}
        onLinked={onLinked}
      />
      <TimelineSection
        records={records}
        files={files}
        docs={docs}
        customer={customer}
        navigate={navigate}
      />
      <HubFiles customer={customer} hub={hub} permissions={permissions} />
    </div>
  );
}

function HubAside({
  hub,
  records,
  assigned,
  customer,
  navigate,
  permissions,
  onEdit,
}: {
  hub: ToolHub;
  records: HubRecord[];
  assigned: Parameters<typeof ToolKnowledge>[0]["assigned"];
  customer: Customer;
  navigate: OperationsNavigate;
  permissions: HubPermissions;
  onEdit: () => void;
}) {
  const { primary } = usePrimaryContact(customer.id, customer.ready);
  return (
    <aside className="space-y-6" aria-label="Tool details">
      <ToolFacts
        hub={hub}
        contact={primary}
        customerSlug={customer.slug}
        canWrite={permissions.canWrite}
        navigate={navigate}
        onEdit={onEdit}
      />
      <ToolKnowledge
        organizationId={customer.id}
        customerSlug={customer.slug}
        customerName={customer.name}
        hub={hub}
        assigned={assigned}
        canAssign={permissions.canWrite}
        canEditCommercial={permissions.canManageBilling}
        canUsePages={permissions.canUsePages}
      />
      <ToolDecisions decisions={currentDecisions(records)} />
    </aside>
  );
}

function HubSheets({
  sheet,
  hub,
  records,
  customer,
  canUpload,
  onClose,
  onSaved,
}: {
  sheet: Sheets;
  hub: ToolHub;
  records: HubRecord[];
  customer: Customer;
  canUpload: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  return (
    <>
      <Sheet open={sheet === "log"} onClose={onClose} title="Log to this tool" kicker={hub.name}>
        <HubComposer
          organizationId={customer.id}
          hubId={hub.id}
          canUpload={canUpload}
          decisions={currentDecisions(records)}
          onAdded={onSaved}
        />
      </Sheet>
      <Sheet open={sheet === "edit"} onClose={onClose} title="Edit tool" kicker={customer.name}>
        <HubEditForm organizationId={customer.id} hub={hub} onCancel={onClose} onDone={onSaved} />
      </Sheet>
    </>
  );
}

function HubHeader({
  hub,
  canWrite,
  onOpen,
}: {
  hub: ToolHub;
  canWrite: boolean;
  onOpen: (sheet: Sheets) => void;
}) {
  return (
    <header className="flex flex-wrap items-end justify-between gap-4">
      <div className="min-w-0">
        <h2 id="tool-hub-title" className="font-heading text-3xl font-extrabold">
          {hub.name}
        </h2>
        {hub.summary ? <p className="mt-1 max-w-3xl text-muted-foreground">{hub.summary}</p> : null}
      </div>
      {canWrite ? (
        <div className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            onClick={() => {
              onOpen("edit");
            }}
          >
            <Pencil /> Edit
          </Button>
          <Button
            onClick={() => {
              onOpen("log");
            }}
          >
            <NotebookPen /> Log a call, email, or note
          </Button>
        </div>
      ) : null}
    </header>
  );
}

function NextStep({ text, due }: { text: string; due: boolean }) {
  return (
    <p
      role="status"
      className={`flex items-center gap-3 rounded-xl px-6 py-4 ${due ? "bg-info-background" : "bg-muted"}`}
    >
      <span className="font-semibold text-link">Next</span>
      <ArrowRight className="size-4 text-link" aria-hidden />
      <span className="font-semibold">{text}</span>
    </p>
  );
}

const TIMELINE_FILTERS: { key: TimelineGroup | "all"; label: string }[] = [
  { key: "all", label: "All" },
  { key: "calls", label: "Calls & meetings" },
  { key: "messages", label: "Emails & texts" },
  { key: "notes", label: "Notes & decisions" },
  { key: "documents", label: "Documents & files" },
];

function TimelineSection({
  records,
  files,
  docs,
  customer,
  navigate,
}: {
  records: HubRecord[];
  files: Artifact[];
  docs: Awaited<ReturnType<typeof getToolHubDocuments>> | null;
  customer: Customer;
  navigate: OperationsNavigate;
}) {
  const [filter, setFilter] = useState<TimelineGroup | "all">("all");
  const timeline = buildTimeline({
    records,
    artifacts: files,
    documents: docs?.documents.linked ?? [],
    organizationId: customer.id,
    slug: customer.slug,
  });
  return (
    <section aria-labelledby="tool-timeline-title" className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <h3 id="tool-timeline-title" className="mr-2 text-xl font-bold">
          Timeline
        </h3>
        <FilterChips options={TIMELINE_FILTERS} value={filter} onChange={setFilter} label="Show" />
      </div>
      <HubTimeline
        entries={filter === "all" ? timeline : timeline.filter((entry) => entry.group === filter)}
        navigate={navigate}
      />
    </section>
  );
}

export function FilterChips<T extends string>({
  options,
  value,
  onChange,
  label,
}: {
  options: readonly { key: T; label: string }[];
  value: T;
  onChange: (value: T) => void;
  label: string;
}) {
  return (
    <div role="group" aria-label={label} className="flex flex-wrap gap-2">
      {options.map((option) => (
        <button
          key={option.key}
          type="button"
          aria-pressed={value === option.key}
          className="min-h-9 rounded-full bg-muted px-4 text-sm font-semibold text-muted-foreground outline-none hover:text-foreground focus-visible:shadow-[var(--focus-ring)] aria-pressed:bg-foreground aria-pressed:text-background"
          onClick={() => {
            onChange(option.key);
          }}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

function HubFiles({
  customer,
  hub,
  permissions,
}: {
  customer: Customer;
  hub: ToolHub;
  permissions: HubPermissions;
}) {
  return (
    <section aria-label="Search and files" className="space-y-6">
      {permissions.canSearch ? (
        <CustomerSearch
          organizationId={customer.id}
          scope={{ kind: "tool", toolId: hub.id }}
          title={`Search ${hub.name}`}
        />
      ) : null}
      <CustomerArtifacts
        organizationId={customer.id}
        ready
        canRead={permissions.canReadFiles}
        canUpload={permissions.canUpload}
        embedded
        toolId={hub.id}
        toolName={hub.name}
      />
    </section>
  );
}

function BackToTools({
  customerSlug,
  navigate,
}: {
  customerSlug: string;
  navigate: OperationsNavigate;
}) {
  return (
    <OperationsLink
      className="inline-flex min-h-11 items-center gap-2 text-link hover:underline"
      href={`/customers/${customerSlug}/tools`}
      navigate={navigate}
    >
      <ArrowLeft className="size-4" /> All tools
    </OperationsLink>
  );
}
